import { listAllTherapistPatientsResponse } from "../../modules/patient/application/patients";
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Users } from 'lucide-react';
import { Patient } from '../../modules/patient/domain/Patient';
import { CouplePatient } from '../../modules/patient/domain/CouplePatient';
import { CouplePatientAPI } from '../../modules/patient/infrastructure/api/CouplePatientApiService';
import { useTherapist } from '../../context/TherapistProvider';
import { useCourseLabels, useSessionPreferences } from '../../modules/center';
import PatientChatTab from '../../modules/patient/ui/components/ViewDetails/PatientChatTab';
import CouplePatientChatTab from '../../modules/patient/ui/components/ViewDetails/CouplePatientChatTab';

type SelectedEntry =
  | { type: 'individual'; patient: Patient }
  | { type: 'couple'; couplePatient: CouplePatient };

interface AlchelyChatModalProps {
  onClose: () => void;
}

export default function AlchelyChatModal({ onClose }: AlchelyChatModalProps) {
  const { t } = useTranslation();
  const { therapist } = useTherapist();
  // A training centre's student is named with the course they are on.
  const { labelOf } = useCourseLabels();
  // Couples only where the centre runs them.
  const { modes } = useSessionPreferences();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [couplePatients, setCouplePatients] = useState<CouplePatient[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [patientError, setPatientError] = useState(false);
  const [selected, setSelected] = useState<SelectedEntry | null>(null);
  const [search, setSearch] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(true);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Fetch individual and couple patients in parallel on mount
  useEffect(() => {
    if (!therapist?.id) return;
    Promise.all([
      listAllTherapistPatientsResponse(therapist.id),
      CouplePatientAPI.listByTherapist(therapist.id),
    ])
      .then(([patientsRes, couplesRes]) => {
        setPatients(patientsRes.data.patients ?? []);
        setCouplePatients(couplesRes.data ?? []);
      })
      .catch(() => setPatientError(true))
      .finally(() => setIsLoadingPatients(false));
  }, [therapist?.id]);

  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus close button on open
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  const filteredPatients = patients.filter((p) => {
    return labelOf(p).toLowerCase().includes(search.toLowerCase());
  });

  const filteredCouplePatients = (modes.couple ? couplePatients : []).filter((c) => {
    const name = c.display_name ?? '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const selectedDisplayName =
    selected?.type === 'individual'
      ? labelOf(selected.patient)
      : selected?.type === 'couple'
        ? selected.couplePatient.display_name
        : '';

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-60 flex items-end sm:items-center justify-center sm:justify-end sm:p-6"
      onClick={onClose}
    >
      {/* Modal panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alchely-chat-modal-title"
        className="relative flex flex-col w-full h-full sm:w-[480px] sm:h-[600px] sm:rounded-2xl bg-surface-raised shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="alchely-chat-modal-title"
            className="text-base font-semibold text-content-primary"
          >
            {t('alchelyChat.modalTitle')}
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label={t('alchelyChat.close')}
            className="flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Patient selector */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          {isLoadingPatients ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : patientError ? (
            <p className="text-sm text-error-600 dark:text-error-400">{t('common.error')}</p>
          ) : selected && !selectorOpen ? (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-content-secondary">
                {selectedDisplayName}
              </span>
              <button
                onClick={() => setSelectorOpen(true)}
                className="text-xs text-brand-500 hover:text-brand-600 underline"
              >
                {t('common.edit')}
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('alchelyChat.patientSelector.searchPlaceholder')}
                className="w-full mb-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                {filteredPatients.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setSelected({ type: 'individual', patient: p }); setSelectorOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      selected?.type === 'individual' && selected.patient.id === p.id
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 font-medium'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-content-secondary'
                    }`}
                  >
                    {labelOf(p)}
                  </button>
                ))}
                {filteredCouplePatients.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`couple-entry-${c.id}`}
                    onClick={() => { setSelected({ type: 'couple', couplePatient: c }); setSelectorOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                      selected?.type === 'couple' && selected.couplePatient.id === c.id
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 font-medium'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-content-secondary'
                    }`}
                  >
                    <Users className="w-3.5 h-3.5 shrink-0 text-content-tertiary" aria-hidden="true" />
                    <span>{c.display_name}</span>
                    <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-content-tertiary">
                      {t('patient.chat.couplePatientBadge')}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-hidden">
          {selected?.type === 'individual' ? (
            <PatientChatTab patient={selected.patient} />
          ) : selected?.type === 'couple' ? (
            <CouplePatientChatTab couplePatient={selected.couplePatient} />
          ) : (
            <div className="flex items-center justify-center h-full px-4">
              <p className="text-sm text-center text-content-tertiary">
                {t('alchelyChat.patientSelector.noPatientPrompt')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
